import type React from 'react'
import { outPortPoint } from '@/lib/text-linking/text-linking-geometry'

import type { ChromeFrame } from '../helpers/chrome-frame-of'
import type { PortControlProps } from '../types/port-control'
import { LinkedOutPortSquare } from './linked-out-port-square'
import { OutPortPlusGlyph } from './out-port-plus-glyph'

/**
 * A frame's out-port control: the unlink square when the frame already links onward, otherwise
 * the "+" that starts a link. Shared by the unlocked out-port pass and the locked (disabled) one,
 * which differ only in the handlers and cursor they hand in.
 */
export function FrameOutPort({
  chrome,
  portNumberOffset,
  rotationDeg,
  linked,
  inLinked,
  seq,
  showContainer,
  control,
  hovered,
  onUnlinkClick,
  onHoverChange,
  plusCursor,
  onPlusClick,
}: {
  chrome: ChromeFrame
  portNumberOffset: number
  rotationDeg: number
  /** The frame links onward → unlink square; otherwise the "+". */
  linked: boolean
  /** The frame has an incoming link, so its number lives at its in-port instead. */
  inLinked: boolean
  seq: number | undefined
  showContainer: boolean
  control: PortControlProps
  hovered: boolean
  onUnlinkClick: () => void
  onHoverChange: (hovered: boolean) => void
  /** Cursor on the "+"'s wrapper group. */
  plusCursor: string
  /** Click on the "+"'s wrapper group; absent where the "+" is inert. */
  onPlusClick?: () => void
}): React.JSX.Element {
  if (linked) {
    return (
      <LinkedOutPortSquare
        center={outPortPoint(chrome, portNumberOffset, chrome.pivot)}
        rotationDeg={rotationDeg}
        // Per the design, a chain's first frame is numbered at its bottom and every
        // follower at its top — never both edges of one frame. A frame with an incoming
        // link already shows `seq` at its in-port, so its out-port square renders
        // unnumbered and serves purely as the unlink affordance. Only a chain head
        // reaches this with a number, i.e. always "1". Matches `ChainConnectors`' passive
        // out-port, which likewise numbers the head only.
        seq={inLinked ? undefined : seq}
        hovered={hovered}
        onClick={onUnlinkClick}
        onHoverChange={onHoverChange}
        control={control}
      />
    )
  }

  return (
    <g onClick={onPlusClick} cursor={plusCursor}>
      <OutPortPlusGlyph
        // Unlinked out-port ("+") stays un-offset, exactly on the border, so it remains
        // pixel-aligned with `TextOverflowBadge` (see that component's doc comment) — which
        // counter-rotates out of its frame's rotation to match this glyph's upright treatment, so
        // the alignment holds at every angle. Only ports that actually show a chain-sequence
        // number get pushed outward.
        center={outPortPoint(chrome, undefined, chrome.pivot)}
        rotationDeg={rotationDeg}
        showContainer={showContainer}
        control={control}
      />
    </g>
  )
}
